import { useQuery } from "@tanstack/react-query";
import { CircleCheck, CloudOff, Database } from "lucide-react";
import type { ReactNode } from "react";

import { fetchReadiness, type Readiness } from "@/lib/api";
import { cn } from "@/lib/cn";

type Tone = "ok" | "degraded" | "offline";

const TONE_CLASSES: Record<Tone, string> = {
  ok: "text-success",
  degraded: "text-warning",
  offline: "text-danger",
};

function Badge({ tone, icon, children }: { tone: Tone; icon: ReactNode; children: ReactNode }) {
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 rounded-full border border-white/10 px-2.5 py-1 text-xs font-medium",
        TONE_CLASSES[tone],
      )}
    >
      {icon}
      {children}
    </span>
  );
}

/**
 * Indicador de conexión del POS.
 *
 * Distingue TRES estados, y esa distinción es la diferencia entre un mensaje
 * útil y un "error" que no le dice nada al cajero:
 *
 *   ok        → la API y la base de datos responden
 *   degraded  → la API vive pero la base no (la venta va a fallar)
 *   offline   → no hay respuesta (se cayó la red o la API)
 *
 * Distinguir "degraded" de "offline" es posible sólo porque el backend separa
 * liveness de readiness. Es un ejemplo de que el diseño del backend se ve en
 * la cara del usuario final.
 */
export function HealthIndicator() {
  const { data, isPending, isError } = useQuery<Readiness>({
    queryKey: ["health", "ready"],
    queryFn: ({ signal }) => fetchReadiness(signal),
    refetchInterval: 15_000,
    retry: false,
    staleTime: 5_000,
  });

  if (isPending) {
    return (
      <span className="text-placeholder inline-flex items-center gap-2 text-xs">
        <span className="bg-placeholder inline-block size-2 animate-pulse rounded-full" />
        Comprobando…
      </span>
    );
  }

  if (isError || !data) {
    return (
      <Badge tone="offline" icon={<CloudOff aria-hidden className="size-3.5" />}>
        Sin conexión con la API
      </Badge>
    );
  }

  if (data.status === "degraded") {
    const fallos = Object.entries(data.checks)
      .filter(([, check]) => check.status === "error")
      .map(([name]) => name)
      .join(", ");
    return (
      <Badge tone="degraded" icon={<Database aria-hidden className="size-3.5" />}>
        Base de datos sin respuesta{fallos ? ` (${fallos})` : ""}
      </Badge>
    );
  }

  return (
    <Badge tone="ok" icon={<CircleCheck aria-hidden className="size-3.5" />}>
      API y base de datos OK
    </Badge>
  );
}
